// Detalle de una partida del presupuesto, integrado en la pestaña:
//   · mode "docked": columna a la derecha de la tabla (pantallas anchas).
//   · mode "inline": ficha desplegada bajo la partida (pestaña estrecha).
// Sustituye al antiguo "Por qué": reúne en un sitio el importe y su fórmula,
// de dónde sale en el pliego, cómo lo ha calculado Lizzi, la tarifa aplicada,
// los campos editables, la nota del equipo y la validación. Se navega con
// ↑/↓ (o J/K) entre partidas y se cierra con Esc.
import React from 'react';
import { Icon } from '../icons.jsx';
import { EXTERNAL_COST_FACTOR } from '../../supabase/functions/_shared/budget-catalog.js';
import { tariffSnapshot, toNum } from '../../supabase/functions/_shared/budget-calc.js';
import {
  fmtEur, fmtEur0, fmtNum, fmtPct, UNIT_SHORT, TagPill, TagPicker, TariffPicker, NumField, TextField, tagTone,
} from './budget-ui.jsx';

const { useEffect, useRef } = React;
const FACTOR_TXT = String(EXTERNAL_COST_FACTOR).replace('.', ',');

const ORIGIN = {
  pliego: { label: 'Necesidad del pliego', cls: 'is-pliego' },
  implicito: { label: 'Coste implícito', cls: 'is-implicito' },
  manual: { label: 'Añadida por el equipo', cls: 'is-manual' },
};
const AMOUNTS = {
  tarifario: { label: 'Importes del tarifario', cls: 'is-tarifario' },
  estimacion: { label: 'Coste estimado por Lizzi', cls: 'is-estimacion' },
  manual: { label: 'Importes fijados a mano', cls: 'is-manual' },
  calculado: { label: `Tarifa = coste × ${FACTOR_TXT}`, cls: 'is-calculado' },
  pendiente: { label: 'Falta el coste', cls: 'is-pendiente' },
};

// "Pliego Técnico CIEC.pdf, p. 14, 15, 16; Pliego Administrativo CIEC.pdf, p. 61"
export function parseSources(source) {
  return String(source || '').split(/\s*;\s*/).map((s) => s.trim()).filter(Boolean).map((s) => {
    const m = s.match(/^(.*?)(?:[,·-]\s*(?:p\.|págs?\.?|páginas?|pp\.)\s*(.+))$/i);
    const doc = (m ? m[1] : s).trim();
    const pages = m ? m[2].trim() : '';
    return { doc, pages, pdf: /\.pdf\b/i.test(doc) };
  });
}

// Extrae la operación del razonamiento de Lizzi ("Cálculo: 1.760 h/año * 2 años = 3.520 horas"
// o "4 itinerarios/año * 10 horas * 2 años = 80 horas") para mostrarla como fichas.
export function parseRationale(text) {
  const t = String(text || '').trim();
  const sentences = t.split(/(?<=\.)\s+|;\s*/);
  const idx = sentences.findIndex((x) => x.includes('=') && /\s[×*x]\s/.test(x));
  if (idx < 0) return { text: t, factors: null, result: null };
  const expr = sentences[idx].replace(/^.*?(?:c[aá]lculo|operaci[oó]n)\s*:\s*/i, '').replace(/\.$/, '').trim();
  const eq = expr.indexOf('=');
  const factors = expr.slice(0, eq).split(/\s+[×*x]\s+/).map((x) => x.trim()).filter(Boolean);
  const result = expr.slice(eq + 1).trim();
  const prefix = sentences[idx].match(/^(.*?)(?:c[aá]lculo|operaci[oó]n)\s*:/i);
  const rest = [...sentences.slice(0, idx), prefix && prefix[1].trim() ? prefix[1].trim() : null, ...sentences.slice(idx + 1)]
    .filter(Boolean).join(' ').trim();
  if (factors.length < 2 || !result) return { text: t, factors: null, result: null };
  return { text: rest, factors, result, raw: expr };
}

function Section({ title, children, aside }) {
  return (
    <section className="bd-section">
      <div className="bd-section-head"><h3>{title}</h3>{aside}</div>
      {children}
    </section>
  );
}

function BudgetDrawer({ mode = 'docked', row: r, c, position, total, catalog, locked, onClose, onPrev, onNext, update, changeTariff, duplicateRow, delRow, onValidateNext, needsReview }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape' && !document.querySelector('.pp')) { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); onNext(); }
      if (e.key === 'ArrowUp' || e.key === 'k') { e.preventDefault(); onPrev(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev]);

  useEffect(() => { panelRef.current?.querySelector('.bd-body')?.scrollTo({ top: 0 }); }, [r?.id]);

  if (!r || !c) return null;
  const t = catalog.byId[r.tariffId];
  const unit = t ? (UNIT_SHORT[t.unit] || t.unit) : 'ud';
  const origin = ORIGIN[r.origin] || ORIGIN.manual;
  const amounts = AMOUNTS[c.costSource] || AMOUNTS.pendiente;
  const margin = c.total > 0 ? ((c.total - c.totalCost) / c.total) * 100 : null;
  const sources = parseSources(r.source);
  const why = parseRationale(r.rationale);
  const review = needsReview(r, c);
  const dP = toNum(r.discountPrice);
  const dC = toNum(r.discountCost);
  const formula = (unitAmt, disc, res) => (unitAmt == null
    ? '—'
    : `${fmtNum(c.units)} ${unit} × ${fmtEur(unitAmt)}${disc ? ` × (1 − ${fmtNum(disc)} %)` : ''} = ${fmtEur(res)}`);

  return (
    <section ref={panelRef} className={`bd is-${mode}`} aria-label={`Detalle de la partida ${position}`}>
      <header className="bd-head">
        <div className="bd-head-row">
          <span className="bd-pos">Partida {position} de {total}{r.tag ? ` · ${r.tag}` : ''}</span>
          <div className="bd-nav">
            <button className="bd-icon" onClick={onPrev} aria-label="Partida anterior" title="Anterior (↑ o K)" disabled={position <= 1}><Icon.ChevronUp size={14}/></button>
            <button className="bd-icon" onClick={onNext} aria-label="Partida siguiente" title="Siguiente (↓ o J)" disabled={position >= total}><Icon.ChevronDown size={14}/></button>
            <button className="bd-icon is-plain" onClick={onClose} aria-label="Cerrar" title="Cerrar (Esc)"><Icon.X size={15}/></button>
          </div>
        </div>
        <div className="bd-title">
          <TextField value={r.concept} strong multiline rows={2} submitOnEnter disabled={locked} ariaLabel="Concepto" placeholder="¿Qué hay que presupuestar?"
            onCommit={(v) => update(r.id, { concept: v.replace(/\s*\n\s*/g, ' ') })}/>
        </div>
        <div className="bd-chips">
          <span className={`bd-chip ${origin.cls}`}>{origin.label}</span>
          {r.tag && <TagPill tag={r.tag} tone={tagTone(r.tag, catalog.tagByName)} size="sm"/>}
          <span className={`bd-chip ${amounts.cls}`}>{amounts.label}</span>
          {r.reviewed && <span className="bd-chip is-ok"><Icon.Check size={11}/> Validada</span>}
        </div>
      </header>

      <div className="bd-body">
        {review && (
          <div className={`bd-alert${c.costSource === 'pendiente' ? ' is-danger' : ''}`}>
            <Icon.AlertTriangle size={14}/>
            <span>{c.costSource === 'pendiente'
              ? 'Falta el coste unitario: complétalo abajo o elige una partida del tarifario.'
              : 'El coste lo ha estimado Lizzi. Revísalo y valídalo o ajústalo.'}</span>
          </div>
        )}

        <div className="bd-money">
          <div className="bd-kpis">
            <div><span>Total</span><strong title={fmtEur(c.total)}>{fmtEur0(c.total)}</strong></div>
            <div><span>Coste</span><strong title={fmtEur(c.totalCost)}>{fmtEur0(c.totalCost)}</strong></div>
            <div><span>Margen</span><strong className={margin != null && margin < 0 ? 'is-neg' : ''}>{fmtPct(margin)}</strong></div>
          </div>
          <div className="bd-formula">
            <div><em>Venta</em>{formula(c.unitPrice, dP, c.total)}</div>
            <div><em>Coste</em>{formula(c.unitCost, dC, c.totalCost)}</div>
          </div>
        </div>

        <Section title="De dónde sale">
          {sources.length ? sources.map((s, i) => (
            <div key={i} className="bd-doc">
              <span className={`bd-doc-icon${s.pdf ? ' is-pdf' : ''}`}><Icon.FileText size={15}/></span>
              <div className="bd-doc-text">
                <strong>{s.doc}</strong>
                <span>{s.pages ? `Página${/[,y-]/.test(s.pages) ? 's' : ''} ${s.pages}` : 'Sin página indicada'}</span>
              </div>
            </div>
          )) : <p className="bd-muted">{r.origin === 'manual' ? 'Partida añadida por el equipo.' : 'Lizzi no indicó la fuente.'}</p>}
        </Section>

        {(why.text || why.factors) && (
          <Section title="Cómo lo ha calculado Lizzi">
            {why.text && <p className="bd-text">{why.text}</p>}
            {why.factors && (
              <div className="bd-calc" title={why.raw}>
                {why.factors.map((f, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="bd-op">×</span>}
                    <span className="bd-token">{f}</span>
                  </React.Fragment>
                ))}
                <span className="bd-op">=</span>
                <span className="bd-token is-result">{why.result}</span>
              </div>
            )}
          </Section>
        )}

        <Section title="Tarifa aplicada">
          <div className="bd-tariff">
            <TariffPicker value={r.tariffId} catalog={catalog} disabled={locked} snapName={r.tariffSnap?.name} onChange={(id) => changeTariff(r, id)}/>
          </div>
          {t && (t.rate != null && t.cost != null
            ? <p className="bd-muted">Tarifa {fmtEur(c.priceStale ? c.liveRate : (r.tariffSnap?.rate ?? t.rate))} · coste {fmtEur(c.costStale ? c.liveCost : (r.tariffSnap?.cost ?? t.cost))} por {unit}{t.desc ? ` · ${t.desc}` : ''}</p>
            : <p className="bd-muted">Esta partida no tiene importe en el tarifario: se usa el coste estimado y la tarifa es coste × {FACTOR_TXT}.</p>)}
          {!r.tariffId && <p className="bd-muted">Sin partida del tarifario: la tarifa es coste × {FACTOR_TXT}.</p>}
          {(c.priceStale || c.costStale) && !locked && (
            <div className="bd-alert is-soft">
              <Icon.Refresh size={13}/>
              <span>El tarifario ha cambiado desde que se eligió: ahora {fmtEur(c.liveRate)} · coste {fmtEur(c.liveCost)}.</span>
              <button className="btn btn-outline btn-sm" onClick={() => update(r.id, { tariffSnap: tariffSnapshot(r.tariffId, catalog.byId) })}>Actualizar</button>
            </div>
          )}
        </Section>

        <Section title="Importes">
          <div className="bd-fields">
            <label className="bd-field"><span>Uds/h</span>
              <NumField ghost={false} value={r.units} disabled={locked} suffix={unit} onCommit={(v) => update(r.id, { units: v ?? 0 })}/>
            </label>
            <div className="bd-field"><span>Etiqueta</span>
              <div className="bd-tagbox"><TagPicker value={r.tag} tags={catalog.tags} tagByName={catalog.tagByName} disabled={locked} onChange={(v) => update(r.id, { tag: v })}/></div>
            </div>
            <label className="bd-field"><span>Tarifa unitaria {toNum(r.unitPriceManual) != null && <button type="button" className="bd-reset" onClick={() => update(r.id, { unitPriceManual: null })}>restaurar</button>}</span>
              <NumField ghost={false} value={c.unitPrice == null ? null : Math.round(c.unitPrice * 100) / 100} disabled={locked} suffix="€" placeholder="—"
                onCommit={(v) => update(r.id, { unitPriceManual: v })}/>
            </label>
            <label className="bd-field"><span>Coste unitario {toNum(r.unitCostManual) != null && <button type="button" className="bd-reset" onClick={() => update(r.id, { unitCostManual: null })}>restaurar</button>}</span>
              <NumField ghost={false} value={c.unitCost == null ? null : Math.round(c.unitCost * 100) / 100} disabled={locked} suffix="€" placeholder="Falta" invalid={c.costSource === 'pendiente'}
                onCommit={(v) => update(r.id, { unitCostManual: v, reviewed: true })}/>
            </label>
            <label className="bd-field"><span>Reducción tarifa</span>
              <NumField ghost={false} value={r.discountPrice} disabled={locked} suffix="%" placeholder="0"
                onCommit={(v) => update(r.id, { discountPrice: v == null ? null : Math.min(100, v) })}/>
            </label>
            <label className="bd-field"><span>Reducción coste</span>
              <NumField ghost={false} value={r.discountCost} disabled={locked} suffix="%" placeholder="0"
                onCommit={(v) => update(r.id, { discountCost: v == null ? null : Math.min(100, v) })}/>
            </label>
          </div>
        </Section>

        <Section title="Nota del equipo">
          <div className="bd-note">
            <TextField value={r.note} multiline rows={3} disabled={locked} placeholder="Decisiones, dudas o supuestos sobre esta partida"
              onCommit={(v) => update(r.id, { note: v })}/>
          </div>
        </Section>
      </div>

      {!locked && (
        <footer className="bd-foot">
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-outline btn-sm" onClick={() => duplicateRow(r)}><Icon.Copy size={12}/> Duplicar</button>
            <button className="btn btn-outline btn-sm bd-danger" onClick={() => delRow(r.id)}><Icon.Trash size={12}/> Eliminar</button>
          </div>
          {review && c.costSource !== 'pendiente'
            ? <button className="btn btn-primary btn-sm" onClick={onValidateNext}><Icon.Check size={13}/> Validar y siguiente</button>
            : <button className="btn btn-outline btn-sm" onClick={onNext} disabled={position >= total}>Siguiente <Icon.ChevronDown size={13}/></button>}
        </footer>
      )}
    </section>
  );
}

export { BudgetDrawer };
